import React, { useRef, useState } from "react"
import { useQuery } from "@apollo/client"
import { Theme, Button } from "@pga/pga-component-library"
import {
  Box,
  SubTitle,
  Text,
  Link,
  RequirementsText,
  TooltipTarget,
  StyledTooltip,
  DisabledText,
  Title,
  ApplicationActions,
} from "./styles"
import { COACH_TOOLS_API } from "../../../../apolloClient"
import { withProspectQuery } from "../../../../utils/prospect/withProspectQuery"
import { withPtmApplicationsQuery } from "../../../../utils/prospect/withPtmApplicationsQuery"
import {
  isDraftPtmApplication,
  pickLatestApplicationForType,
  ptmApplyCtaLabel,
  PTM_APPLICATION_TYPES,
} from "../../../../utils/prospect/ptmApplicationStatus"
import {
  isReapplyPathEligible,
  reapplyPathActionState,
} from "../../../../utils/reapplyPathOffer"
import { AFFILIATE_APPLICATION_ELIGIBILITY_QUERY } from "../../../queries/affiliateApplicationEligibility"
import { ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY } from "../../../queries/associateApplicationEligibility"
import PtmApplicationStatus from "../../../PtmApplicationStatus"
import calculateRequirementsMet, {
  affiliatePathRequirementsPassed,
  associateRequirementsPassed,
  isAssociateRequirementsMet,
} from "../../../../utils/prospect/calculateRequirements"
import { PathBoxLoader } from "../WidgetStates"
import { redirectToMemberApplications } from "../../../../lib/ptmApplicationsRedirect"

export const renderPassedRequirements = requirements =>
  Object.entries(requirements).map(([requirement, isPassed], index) => (
    <div key={index}>
      {isPassed ? requirement : <DisabledText>{requirement}</DisabledText>}
    </div>
  ))

/** @deprecated Prefer PtmApplicationStatus — kept for existing PathBox tests. */
export const ApplicationStatus = ({ status, date }) => (
  <PtmApplicationStatus status={status} date={date} />
)

export const AssociateBox = ({
  data,
  application,
  associateEligibility = null,
}) => {
  const [associateTooltipOpen, setAssociateTooltipOpen] = useState(false)
  const passedRequirements = associateRequirementsPassed(data)
  const associateTooltip = useRef(null)
  const requirementsMet = isAssociateRequirementsMet(data)
  const associateAction = reapplyPathActionState(
    application,
    requirementsMet,
    associateEligibility?.pathEligible,
  )
  const leftoverDraft =
    isDraftPtmApplication(application?.status) && !associateAction.isResume
  const showAwaitingRequirements =
    !leftoverDraft &&
    !associateAction.replaceApplyCta &&
    !associateAction.isResume &&
    !associateAction.showApplyCta
  const showButton = associateAction.showApplyCta || showAwaitingRequirements
  const associateCtaLabel = ptmApplyCtaLabel({
    applicationType: PTM_APPLICATION_TYPES.ASSOCIATE,
    requirementsMet: !showAwaitingRequirements,
    isResume: associateAction.isResume,
    isClosedRegistration: associateAction.isClosedRegistration,
  })

  return (
    <Box bg>
      <div className="container">
        <SubTitle>
          Associate{" "}
          <span style={{ color: Theme.colors.deepGrey }}>(Recommended)</span>
        </SubTitle>
        <Text>
          To become a PGA Member, you'll need to become a registered Associate
          before completing the PGA Professional Golf Management (PGA PGM)
        </Text>
        <Text>
          <Link
            href="https://www.pga.org/membership/associate-program"
            target="_blank"
            rel="noreferrer"
          >
            Learn More &rarr;
          </Link>
        </Text>
        <RequirementsText>
          Requirements Met: &nbsp;
          <TooltipTarget ref={associateTooltip} id="associateTooltip">
            {calculateRequirementsMet(passedRequirements)}
          </TooltipTarget>
          <StyledTooltip
            isOpen={associateTooltipOpen}
            placement="bottom"
            autohide={false}
            target={associateTooltip}
            toggle={() => setAssociateTooltipOpen(!associateTooltipOpen)}
          >
            {renderPassedRequirements(passedRequirements)}
          </StyledTooltip>
        </RequirementsText>
        {associateAction.replaceApplyCta ? (
          <PtmApplicationStatus
            status={application.status}
            registrationStatus={application.registrationStatus}
            applicationType={PTM_APPLICATION_TYPES.ASSOCIATE}
          />
        ) : showButton || associateAction.showDeniedStatus ? (
          <ApplicationActions>
            {associateAction.showDeniedStatus ? (
              <PtmApplicationStatus
                applicationId={application.id}
                status={application.status}
                date={application.updatedAt}
                applicationType={PTM_APPLICATION_TYPES.ASSOCIATE}
              />
            ) : null}
            {showButton ? (
              <Button
                onClick={redirectToMemberApplications}
                disabled={showAwaitingRequirements}
              >
                {associateCtaLabel}
              </Button>
            ) : null}
          </ApplicationActions>
        ) : null}
      </div>
    </Box>
  )
}

export const AffiliateBox = ({
  data,
  application,
  affiliateEligible = false,
  affiliateEligibility = null,
}) => {
  const [affiliateTooltipOpen, setAffiliateTooltipOpen] = useState(false)
  const affiliateTooltip = useRef(null)
  const affiliateAction = reapplyPathActionState(
    application,
    affiliateEligible,
    affiliateEligibility?.pathEligible,
  )
  const leftoverDraft =
    isDraftPtmApplication(application?.status) && !affiliateAction.isResume
  const passedRequirements = affiliatePathRequirementsPassed(
    data,
    affiliateEligibility,
  )
  const showAwaitingRequirements =
    !leftoverDraft &&
    !affiliateAction.replaceApplyCta &&
    !affiliateAction.isResume &&
    !affiliateAction.showApplyCta
  const showButton = affiliateAction.showApplyCta || showAwaitingRequirements
  const affiliateCtaLabel = ptmApplyCtaLabel({
    applicationType: PTM_APPLICATION_TYPES.AFFILIATE,
    requirementsMet: !showAwaitingRequirements,
    isResume: affiliateAction.isResume,
    isClosedRegistration: affiliateAction.isClosedRegistration,
  })

  return (
    <Box>
      <div className="container">
        <SubTitle>Affiliate</SubTitle>
        <Text>
          Want to become a PGA Member, but not yet working in golf? Begin in the
          Affiliate Program, to eventually become an PGA Associate.
        </Text>
        <Text>
          <Link
            href="https://www.pga.org/membership/affiliate-program"
            target="_blank"
            rel="noreferrer"
          >
            Learn More &rarr;
          </Link>
        </Text>
        <RequirementsText>
          Requirements Met: &nbsp;
          <TooltipTarget ref={affiliateTooltip} id="affiliateTooltip">
            {calculateRequirementsMet(passedRequirements)}
          </TooltipTarget>
          <StyledTooltip
            isOpen={affiliateTooltipOpen}
            placement="bottom"
            autohide={false}
            target={affiliateTooltip}
            toggle={() => {
              setAffiliateTooltipOpen(!affiliateTooltipOpen)
            }}
          >
            {renderPassedRequirements(passedRequirements)}
          </StyledTooltip>
        </RequirementsText>
        {affiliateAction.replaceApplyCta ? (
          <PtmApplicationStatus
            status={application.status}
            registrationStatus={application.registrationStatus}
            applicationType={PTM_APPLICATION_TYPES.AFFILIATE}
          />
        ) : showButton || affiliateAction.showDeniedStatus ? (
          <ApplicationActions>
            {affiliateAction.showDeniedStatus ? (
              <PtmApplicationStatus
                applicationId={application.id}
                status={application.status}
                date={application.updatedAt}
                applicationType={PTM_APPLICATION_TYPES.AFFILIATE}
              />
            ) : null}
            {showButton ? (
              <Button
                onClick={redirectToMemberApplications}
                disabled={showAwaitingRequirements}
                outline={!showAwaitingRequirements}
              >
                {affiliateCtaLabel}
              </Button>
            ) : null}
          </ApplicationActions>
        ) : null}
      </div>
    </Box>
  )
}

export const PathStack = ({
  data,
  membershipApplications,
  affiliateEligible = false,
  affiliateEligibility = null,
  associateEligibility = null,
}) => {
  const associateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.ASSOCIATE,
  )
  const affiliateApplication = pickLatestApplicationForType(
    membershipApplications,
    PTM_APPLICATION_TYPES.AFFILIATE,
  )

  return (
    <>
      <div className="row">
        <div className="col">
          <AssociateBox
            data={data}
            application={associateApplication}
            associateEligibility={associateEligibility}
          />
        </div>
        <div className="col">
          <AffiliateBox
            data={data}
            application={affiliateApplication}
            affiliateEligible={affiliateEligible}
            affiliateEligibility={affiliateEligibility}
          />
        </div>
      </div>
    </>
  )
}

const PathBox = ({
  currentMember,
  loading,
  membershipApplications,
  ptmApplicationsLoading,
}) => {
  const { data: affiliateEligibilityData, error: affiliateEligibilityError } =
    useQuery(AFFILIATE_APPLICATION_ELIGIBILITY_QUERY, {
      context: { api: COACH_TOOLS_API },
      fetchPolicy: "network-only",
    })
  const { data: associateEligibilityData, error: associateEligibilityError } =
    useQuery(ASSOCIATE_APPLICATION_ELIGIBILITY_QUERY, {
      context: { api: COACH_TOOLS_API },
      fetchPolicy: "network-only",
    })
  const affiliateEligibilityLoading =
    !affiliateEligibilityData && !affiliateEligibilityError
  const associateEligibilityLoading =
    !associateEligibilityData && !associateEligibilityError
  const affiliateEligibility =
    affiliateEligibilityData?.affiliateApplicationEligibility
  const associateEligibility =
    associateEligibilityData?.associateApplicationEligibility
  const affiliateEligible = isReapplyPathEligible({
    shouldFetch: true,
    loading: affiliateEligibilityLoading,
    error: affiliateEligibilityError,
    eligible: affiliateEligibility?.eligible,
  })

  if (
    loading ||
    ptmApplicationsLoading ||
    affiliateEligibilityLoading ||
    associateEligibilityLoading
  ) {
    return <PathBoxLoader />
  }
  return (
    <div className="container">
      <Title marginTop="20px">Path to Membership</Title>
      <span>
        Welcome to Path to Membership! You must complete all of the below
        requirements in no particular order to apply for the Associate program.
        To apply for the Affiliate program, complete the Background Check and be
        Qualifier Eligible.
      </span>
      <Title marginTop="10px" color={Theme.colors.primary}>
        Choose Your Path
      </Title>
      <PathStack
        data={currentMember}
        membershipApplications={membershipApplications}
        affiliateEligible={affiliateEligible}
        affiliateEligibility={affiliateEligibility}
        associateEligibility={associateEligibility}
      />
    </div>
  )
}

export default withProspectQuery(withPtmApplicationsQuery(PathBox))
